import { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ChevronLeft, Flag, Eraser, Info } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { LatexText } from "@/components/ui/latex-text";
import {
  fetchFlaggedQuestionsDetailed,
  setFlaggedQuestion,
  clearFlaggedQuestions,
  mapQuestionRow,
  type PracticeQuestion,
} from "@/lib/api/practice";
import { getSubjectMetadata } from "@/lib/subject-metadata";

const letter = (i: number) => String.fromCharCode(65 + i);

type FlaggedQuestion = PracticeQuestion & { createdAt: string | null };

type OutlineUnit = Awaited<ReturnType<typeof fetchSubjectOutline>>["units"][number];

type OutlineTopic = OutlineUnit["topics"][number];

export default function FlaggedQuestions() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  const subject = outlineQuery.data?.subject ?? null;
  const units = outlineQuery.data?.units ?? [];
  const metadata = getSubjectMetadata(slug ?? "");

  const flaggedQuery = useQuery({
    queryKey: ["flagged-questions", user?.id, subject?.id],
    queryFn: () => fetchFlaggedQuestionsDetailed(user!.id, subject!.id),
    enabled: Boolean(user?.id && subject?.id),
  });

  const flaggedQuestions = useMemo(() => {
    const rows = flaggedQuery.data ?? [];
    return rows
      .map((row) => {
        const question = Array.isArray(row.questions) ? row.questions[0] : row.questions;
        if (!question) return null;
        const normalized = mapQuestionRow(question as any);
        return {
          ...normalized,
          createdAt: row.created_at ?? null,
        } as FlaggedQuestion;
      })
      .filter(Boolean) as FlaggedQuestion[];
  }, [flaggedQuery.data]);

  const unflagMutation = useMutation({
    mutationFn: (questionId: string) => setFlaggedQuestion(user!.id, questionId, false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagged-questions", user?.id, subject?.id] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => clearFlaggedQuestions(user!.id, subject!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flagged-questions", user?.id, subject?.id] });
    },
  });

  const [selections, setSelections] = useState<Record<string, number | undefined>>({});
  const [status, setStatus] = useState<Record<string, "incomplete" | "correct" | "wrong">>({});

  const titleFor = (question: FlaggedQuestion) => {
    const unit = units.find((u) => u.id === question.unitId);
    const unitTitle = (unit?.name ?? (unit as { title?: string })?.title ?? question.unitId) as string;
    const topic = unit?.topics.find((t) => t.slug === question.topicSlug);
    const topicTitle = (topic?.title ?? (topic as { name?: string })?.name ?? question.topicSlug ?? "Topic") as string;
    const topicShort = topicTitle.replace(/\s*:\s*.*/, "");
    return { unitTitle, topicShort };
  };

  const onSelect = (questionId: string, index: number) => {
    setSelections((prev) => ({ ...prev, [questionId]: index }));
    setStatus((prev) => ({ ...prev, [questionId]: "incomplete" }));
  };

  const onCheck = (question: FlaggedQuestion) => {
    const choice = selections[question.id];
    if (choice === undefined || question.correctIndex == null) {
      setStatus((prev) => ({ ...prev, [question.id]: "incomplete" }));
      return;
    }
    const correct = choice === question.correctIndex ? "correct" : "wrong";
    setStatus((prev) => ({ ...prev, [question.id]: correct }));
  };

  const onUnflag = (questionId: string) => {
    unflagMutation.mutate(questionId);
  };

  const onClearAll = () => {
    clearMutation.mutate();
  };

  const subjectTitle = subject?.name ?? slug ?? "Subject";

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Flagged Questions</CardTitle>
              <CardDescription>Sign in to access your flagged questions.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Loading subject…</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-32 rounded-lg bg-muted/40 animate-pulse" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Flagged Questions for {subjectTitle}</CardTitle>
            <CardDescription>
              Review questions you marked with the red flag while practicing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {flaggedQuery.isLoading ? (
              <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
            ) : flaggedQuestions.length === 0 ? (
              <div className="text-center py-12 space-y-4">
                <p className="text-muted-foreground">You don’t have any flagged questions for this subject.</p>
                <div className="flex justify-center gap-2">
                  <Button asChild>
                    <Link to={`/practice/${slug}/setup`}>Start practice</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/dashboard">Go to dashboard</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-muted-foreground">{flaggedQuestions.length} flagged</div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={onClearAll}
                      className="rounded-md"
                      disabled={clearMutation.isPending}
                    >
                      <Eraser className="h-4 w-4 mr-2" /> {clearMutation.isPending ? "Clearing…" : "Clear all"}
                    </Button>
                    <Button asChild>
                      <Link to={`/practice/${slug}/setup`}>Practice more</Link>
                    </Button>
                  </div>
                </div>
                <Accordion type="multiple" className="space-y-3">
                  {flaggedQuestions.map((question, index) => {
                    const loc = titleFor(question);
                    const selection = selections[question.id];
                    const state = status[question.id] ?? "incomplete";
                    const difficultyLabel = question.difficulty
                      ? question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)
                      : "Unknown";
                    const showDifficulty = question.difficulty && question.difficulty !== "unassigned";
                    const difficultyClass =
                      question.difficulty === "easy"
                        ? "bg-emerald-100 text-emerald-700"
                        : question.difficulty === "medium"
                        ? "bg-amber-100 text-amber-700"
                        : question.difficulty === "hard"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-muted text-muted-foreground";
                    const stateBorder =
                      state === "correct"
                        ? "border-emerald-500"
                        : state === "wrong"
                        ? "border-rose-500"
                        : "border-amber-400";
                    const stateBg =
                      state === "correct"
                        ? "bg-emerald-50/60"
                        : state === "wrong"
                        ? "bg-rose-50/60"
                        : "bg-amber-50/50";

                    return (
                      <AccordionItem key={question.id} value={question.id} className={`rounded-md border ${stateBorder} bg-card`}>
                        <AccordionTrigger className={`px-3 py-2 ${stateBg} rounded-md`}>
                          <div className="flex w-full items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="font-medium">Question {index + 1}</span>
                              <Badge variant="secondary">{loc.topicShort}</Badge>
                              {showDifficulty && (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${difficultyClass}`}>
                                  {difficultyLabel}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground capitalize">{state}</div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="p-3 space-y-3">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              {question.kcRef && (
                                <>
                                  <span>This question tests: {question.kcRef}</span>
                                  <Info className="h-3.5 w-3.5" />
                                </>
                              )}
                            </div>
                            <div className="text-base"><LatexText text={question.stem} /></div>
                            <div className="space-y-2">
                              {question.choices.map((choice, choiceIndex) => {
                                const selected = selection === choiceIndex;
                                const isCorrectChoice = state !== "incomplete" && choiceIndex === question.correctIndex;
                                const isIncorrectSelected = state === "wrong" && selected && choiceIndex !== question.correctIndex;
                                const borderClass = isCorrectChoice
                                  ? "border-emerald-500 bg-emerald-50"
                                  : isIncorrectSelected
                                  ? "border-rose-500 bg-rose-50"
                                  : selected
                                  ? "border-primary bg-primary/5"
                                  : "border-muted";
                                return (
                                  <div key={choiceIndex} className="space-y-1">
                                    <label
                                      className={`flex items-center justify-between rounded-md border px-3 py-2 cursor-pointer ${borderClass}`}
                                      onClick={() => onSelect(question.id, choiceIndex)}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div
                                          className={`h-8 w-8 rounded-full flex items-center justify-center border ${
                                            selected
                                              ? "border-primary text-primary"
                                              : "border-muted-foreground/30 text-muted-foreground"
                                          } bg-background`}
                                        >
                                          {letter(choiceIndex)}
                                        </div>
                                        <div className="text-sm"><LatexText text={choice.text} /></div>
                                      </div>
                                    </label>
                                    {state !== "incomplete" && choice.reason && (
                                      <div className="pl-14 pr-3 pb-1">
                                        <div className="text-xs text-muted-foreground italic">
                                          <LatexText text={choice.reason} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex items-center justify-between pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onUnflag(question.id)}
                                disabled={unflagMutation.isPending}
                              >
                                <Flag className="h-4 w-4 mr-2 text-red-600" />
                                Unflag
                              </Button>
                              <div className="flex items-center gap-2">
                                <Button onClick={() => onCheck(question)}>
                                  {state === "incomplete" ? "Check answer" : "Recheck"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}